
import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ManagerDashboardService } from '../../services/manager-dashboard.service';
import { User } from '../../models/user';
import { Router } from '@angular/router';
import { UserTagCrudService } from '../../services/user-tag-crud.service';
import { Tag } from '../../models/tag';
import { UserTagRequest } from '../../models/userTagRequest';
import { Team } from '../../models/team';
import { UserTagsResponse } from '../../models/userTagResponse';
import { UserTagsWithAssignmentNameRequest } from '../../models/userTagsWithAssignmentNameRequest';
import { TenantRouterService } from '../../services/tenant-router.service';

@Component({
  selector: 'app-add-user-tag',
  standalone: false,
  templateUrl: './add-user-tag.component.html',
  styleUrl: './add-user-tag.component.scss'
})
export class AddUserTagComponent implements OnInit, OnDestroy {

  users!: User[];
  tags!: Tag[];
  selectedUser: User[] = [];
  selectedTags: Tag[] = [];
  managerId!: number;
  teamId!: number;
  teams: Team[] = [];
  tagSubscription: Subscription = new Subscription();
  teamSubscription: Subscription = new Subscription();
  userQuestionGroupSubscription: Subscription = new Subscription();
  selectedTeam!: number;
  userTagResponses: UserTagsResponse[] = [];
  assignmentName!: string;
  time!:number;

  //added properties for template//
  selectedUsers: User[] = [];
  selectedAssignments: Tag[] = [];
  userAssignments: UserTagsResponse[] = [];
  selectedTeamName: string = '';
  userDropdownOpen: boolean = false;
  assignmentDropdownOpen: boolean = false;
  userSearchTerm: string = '';
  assignmentSearchTerm: string = '';
  showAssignmentTable: boolean = false;


  @ViewChild('assignmentTable') assignmentTable!: ElementRef;

  constructor(
    private managerDashboardService: ManagerDashboardService,
    private userTagCrudService: UserTagCrudService,
    private router: Router, private tenantRouter: TenantRouterService
  ) {}

  ngOnInit(): void {
    const managerid = sessionStorage.getItem('manager_id');
    if (managerid) {
      this.managerId = parseInt(managerid, 10);
    }
    this.teamSubscription = this.managerDashboardService.getAllTeams(this.managerId).subscribe(
      (teams) => {
        this.teams = teams;
      }
    );
    this.tagSubscription = this.managerDashboardService.getAllTags().subscribe(
      (tags) => {
        this.tags = tags;
      }
    );
  }

  //methods for dropdown functionality//
  toggleDropdown(dropdownType: string): void {
    if (dropdownType === 'user') {
      this.userDropdownOpen = !this.userDropdownOpen;
      this.assignmentDropdownOpen = false;
    } else if (dropdownType === 'assignment') {
      this.assignmentDropdownOpen = !this.assignmentDropdownOpen;
      this.userDropdownOpen = false;
    }
  }

  onDropdownClick(event: Event): void {
    event.stopPropagation();
  }

  toggleUser(user: User): void {
    const index = this.selectedUsers.findIndex(u => u.UserID === user.UserID);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(user);
    }
  }

  toggleAssignment(assignment: Tag): void {
    const index = this.selectedAssignments.findIndex(a => a.tagId === assignment.tagId);
    if (index > -1) {
      this.selectedAssignments.splice(index, 1);
    } else {
      this.selectedAssignments.push(assignment);
    }
  }

  toggleSelectAllUsers(): void {
    if (this.areAllUsersSelected) {
      this.selectedUsers = [];
    } else {
      this.selectedUsers = [...this.searchFilteredUsers];
    }
  }

  toggleSelectAllAssignments(): void {
    const filteredAssignments = this.searchFilteredAssignments;

    if (this.areAllAssignmentsSelected) {
      this.selectedAssignments = this.selectedAssignments.filter(selected =>
        !filteredAssignments.some(filtered => filtered.tagId === selected.tagId)
      );
    } else {
      filteredAssignments.forEach(assignment => {
        if (!this.isAssignmentSelected(assignment)) {
          this.selectedAssignments.push(assignment);
        }
      });
    }
  }

  isUserSelected(user: User): boolean {
    return this.selectedUsers.some(u => u.UserID === user.UserID);
  }

  isAssignmentSelected(assignment: Tag): boolean {
    return this.selectedAssignments.some(a => a.tagId === assignment.tagId);
  }

  get areAllUsersSelected(): boolean {
    const filteredUsers = this.searchFilteredUsers;
    return filteredUsers.length > 0 && this.selectedUsers.length === filteredUsers.length;
  }

  get areAllAssignmentsSelected(): boolean {
    const filteredAssignments = this.searchFilteredAssignments;
    if (filteredAssignments.length === 0) return false;

    return filteredAssignments.every(assignment =>
      this.selectedAssignments.some(selected => selected.tagId === assignment.tagId)
    );
  }

  get searchFilteredUsers(): User[] {
    if (!this.users || !Array.isArray(this.users)) return [];
    if (!this.userSearchTerm) return this.users;
    return this.users.filter(user =>
      user.Name.toLowerCase().includes(this.userSearchTerm.toLowerCase())
    );
  }

  get searchFilteredAssignments(): Tag[] {
    if (!this.tags || !Array.isArray(this.tags)) return [];
    if (!this.assignmentSearchTerm) return this.tags;
    return this.tags.filter(tag =>
      tag.name.toLowerCase().includes(this.assignmentSearchTerm.toLowerCase())
    );
  }

  getTeamMemberCount(team: Team): number {
    return (team as any).memberCount || 0;
  }

  getTeamDisplayText(team: Team): string {
    const memberCount = this.getTeamMemberCount(team);
    return memberCount > 0 ? `${team.name} (${memberCount} members)` : team.name;
  }

  trackByUserId(index: number, item: UserTagsResponse): any {
    return item.user.UserID;
  }

  trackByAssignmentId(index: number, item: Tag): any {
    return item.tagId;
  }

  removeAssignment(userAssignment: UserTagsResponse, assignment: Tag): void {
    const index = userAssignment.tags.findIndex(a => a.tagId === assignment.tagId);
    if (index > -1) {
      userAssignment.tags.splice(index, 1);
      if (userAssignment.tags.length === 0) {
        const userIndex = this.userAssignments.findIndex(ua => ua.user.UserID === userAssignment.user.UserID);
        if (userIndex > -1) {
          this.userAssignments.splice(userIndex, 1);
        }
      }
    }
  }

  getUserAssignments(userTagResponse: UserTagsResponse): Tag[] {
    return userTagResponse.tags || [];
  }

  addTagsToUser() {
    const userTagsWithAssignmentNameRequest: UserTagsWithAssignmentNameRequest = new UserTagsWithAssignmentNameRequest();
    this.userTagResponses.forEach((userTagResponse) => {
      let request: UserTagRequest = new UserTagRequest();
      request.userId = userTagResponse.user.UserID;
      userTagResponse.tags.forEach((tag) => {
        request.tagIds.push(tag.tagId);
      });
      userTagsWithAssignmentNameRequest.userTags.push(request);
    });

    userTagsWithAssignmentNameRequest.assignmentName = this.assignmentName;
    userTagsWithAssignmentNameRequest.managerId = this.managerId;
    userTagsWithAssignmentNameRequest.time = this.time;

    this.userQuestionGroupSubscription = this.userTagCrudService
      .addTagToUser(userTagsWithAssignmentNameRequest)
      .subscribe((response) => {
       this.tenantRouter.navigate('manager-dashboard');
      });
  }

  onTeamChange() {
    this.teamId = Number(this.selectedTeam);
    const selectedTeamObj = this.teams.find(team => team.teamId === this.teamId);
    this.selectedTeamName = selectedTeamObj ? selectedTeamObj.name : '';
    this.selectedUsers = [];
    for (const team of this.teams) {
      if (team.teamId === this.teamId) {
        this.users = team.users;
      }
    }
    this.showAssignmentTable = false;
  }

  assignTagToUser() {
    this.selectedUser = [...this.selectedUsers];
    this.selectedTags = [...this.selectedAssignments];

    for (const user of this.selectedUser) {
      let isUserPresent = false;
      for (const userTagResponse of this.userTagResponses) {
        if (user.UserID === userTagResponse.user.UserID) {
          for (const tag of this.selectedTags) {
            const tagExists = userTagResponse.tags.some(existingTag => existingTag.tagId === tag.tagId);
            if (!tagExists) {
              userTagResponse.tags.push(tag);
            }
          }
          isUserPresent = true;
          break;
        }
      }
      if (!isUserPresent) {
        const userTagResponse = new UserTagsResponse();
        userTagResponse.user = user;
        userTagResponse.tags = [...this.selectedTags];
        this.userTagResponses.push(userTagResponse);
      }
    }

    this.userAssignments = [...this.userTagResponses];
    this.selectedUsers = [];
    this.selectedAssignments = [];
    this.userDropdownOpen = false;
    this.assignmentDropdownOpen = false;

    //table show//
    this.showAssignmentTable = true;
    setTimeout(() => {
      if (this.assignmentTable) {
        this.assignmentTable.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }




  removeTag(user: User, removeTag: Tag) {
    this.userTagResponses.forEach((userTagResponse, index) => {
      if (userTagResponse.user == user) {
        userTagResponse.tags = userTagResponse.tags.filter(tag => tag !== removeTag);
        if (userTagResponse.tags.length === 0) {
          this.userTagResponses.splice(index, 1);
        }
      }
    });
    this.userAssignments = [...this.userTagResponses];
  }

  ngOnDestroy(): void {
    this.tagSubscription.unsubscribe();
    this.teamSubscription.unsubscribe();
    this.userQuestionGroupSubscription.unsubscribe();
  }
}
